// Add timing jitter to a voice via a per-cycle random `.late()` offset.
//
// Strudel-idiomatic: `.late(rand.range(0, jitterCycles))` produces a
// fresh random offset each event, so the voice swings off-grid in a
// way that feels organic. We use the existing AST modulator support
// (Numeric.kind === 'modulator', source: 'rand') so the codegen path
// is the one already covered by tests.
//
// Idempotent: calling twice replaces the existing humanize range
// rather than stacking. Detection is by-shape — a `.late(rand.range(…))`
// effect is treated as the harness's humanize stamp.

import { newBlockId } from '../blocks/types';
import type { EditRecipe } from '../blocks/editContext';
import type { Effect, Numeric, SimpleEffect } from '../blocks/types';
import type { Transform } from './types';

// Default jitter range in cycles. At 124 BPM (cps ≈ 0.516), 0.02 cycles
// ≈ 39 ms — enough to feel loose, not enough to read as wrong.
const DEFAULT_JITTER_CYCLES = 0.02;

export const humanizeTransform: Transform = {
  id: 'humanize',
  category: 'rhythm',
  verb: 'Humanize',
  description: 'Add gentle timing jitter via .late(rand.range(…)).',
  apply({ song, voiceName }) {
    const stmt = song.statements.find(
      (s) => s.kind === 'const' && s.name === voiceName,
    );
    if (!stmt || stmt.kind !== 'const') {
      return { ok: false, reason: `no const named "${voiceName}"` };
    }
    const expr = stmt.expr;
    if (expr.kind !== 'voice.s' && expr.kind !== 'voice.note') {
      return {
        ok: false,
        reason: `"${voiceName}" is not a voice (kind: ${expr.kind})`,
      };
    }
    const recipe: EditRecipe = (draft) => {
      const target = draft.statements.find(
        (s) => s.kind === 'const' && s.name === voiceName,
      );
      if (!target || target.kind !== 'const') return;
      const ex = target.expr;
      if (ex.kind !== 'voice.s' && ex.kind !== 'voice.note') return;
      const existingIdx = ex.effects.findIndex(isHumanizeStamp);
      const fresh = makeHumanizeEffect(DEFAULT_JITTER_CYCLES);
      if (existingIdx >= 0) {
        ex.effects[existingIdx] = fresh;
      } else {
        ex.effects.push(fresh);
      }
    };
    return {
      ok: true,
      recipe,
      summary: `humanize ${voiceName}`,
    };
  },
};

function makeHumanizeEffect(jitterCycles: number): SimpleEffect {
  const range: Numeric = {
    kind: 'modulator',
    source: 'rand',
    range: [
      { kind: 'num', value: 0 },
      { kind: 'num', value: jitterCycles },
    ],
  };
  return {
    id: newBlockId(),
    kind: 'late',
    args: [{ kind: 'numeric', value: range }],
  };
}

function isHumanizeStamp(e: Effect): boolean {
  // `late` is a SimpleEffectKind, so e is necessarily a SimpleEffect
  // here — the kind check filters out conditionals.
  if (e.kind !== 'late') return false;
  if (e.args.length !== 1) return false;
  const arg = e.args[0]!;
  if (arg.kind !== 'numeric') return false;
  return arg.value.kind === 'modulator' && arg.value.source === 'rand';
}
